import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { Bold, ClassicEditor, Essentials, Heading, Italic, Link, Mention, Paragraph, Undo } from 'ckeditor5';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AD_HOC,
  CREATE_EMAIL,
  DRAFT,
  FILTER_OPERATIONS,
  PUBLISH,
  PUBLISHED,
  SENT,
  UPDATE_EMAIL,
  verticalScrollStyle,
} from '../../constants';
import { compareData } from '../../helpers/surveyUtil';
import { FILTERS } from '../../models/global';
import { State } from '../../models/state';
import {
  createEmail,
  getEmailById,
  getEmailByParams,
  resetEmail,
  sendEmail,
  updateEmail,
} from '../../store/actions/email';
import { getOrganizationSurveys, resetOrganizationSurveys } from '../../store/actions/organizationSurvey';
import { getStaticValues, getSurveySeries } from '../../store/actions/survey';
import Confirmation from '../Confirmation/Confirmation';
import Button from '../FormComponents/Button';
import Dropdown from '../FormComponents/Dropdown';
import TextField from '../FormComponents/TextField';
import NoData from '../NoData/NoData';
import ToastMessage from '../ToastMessage/ToastMessage';

interface EmailFormProps {
  type: string;
}

function EmailForm(props: EmailFormProps) {
  const { type } = props;
  const { emailId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const editor = useRef<ClassicEditor>();
  const { organizationSurveys, dropdownValues } = useSelector((state: State) => state.organizationSurvey);
  const { languages, emailTypes, seriesList } = useSelector((state: State) => state.survey);
  const { email } = useSelector((state: State) => state.email);
  const [emails, setEmails] = useState({});
  const [dbEmails, setDbEmails] = useState({});
  const [language, setLanguage] = useState('English');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [toast, setToast] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [filterByField, setFilterByField] = useState('');
  const [filterByValue, setFilterByValue] = useState('');
  const [filterValues, setFilterValues] = useState<string[]>([]);
  const [filterOperation, setFilterOperation] = useState('');
  const [filters, setFilters] = useState<FILTERS[]>([]);
  const [dbFilters, setDBFilters] = useState<FILTERS[]>([]);
  const [emailType, setEmailType] = useState('');
  const [confirmationPopupText, setConfirmationPopupText] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [selectedSurveyInfo, setSelectedSurveyInfo] = useState<{
    surveyId: string;
    surveySeriesId: string;
  }>({
    surveyId: '',
    surveySeriesId: '',
  });
  const [organizationSurveysLoading, setOrganizationSurveysLoading] = useState(false);

  const isSameData = compareData(emails, dbEmails) && compareData(filters, dbFilters);

  const readonly = email?.status === SENT && type === UPDATE_EMAIL;

  const getSurveyData = () => {
    const surveySeriesNameExistsExactlyOne = filters.find(
      filter =>
        filter.field === 'Survey series name' &&
        filter.operation === FILTER_OPERATIONS[0] &&
        filter.values.length === 1,
    );
    const surveyNameExistsExactlyOne = filters.find(
      filter => filter.field === 'Survey' && filter.operation === FILTER_OPERATIONS[0] && filter.values.length === 1,
    );

    const organizationSurvey = organizationSurveys.find(
      el =>
        el.survey === surveyNameExistsExactlyOne?.values?.[0] &&
        el.surveySeriesName === surveySeriesNameExistsExactlyOne?.values?.[0],
    );
    return {
      surveySeriesNameExistsExactlyOne,
      surveyNameExistsExactlyOne,
      organizationSurvey,
    };
  };

  const getEmailsToBeSentTo = () => {
    if (!organizationSurvey) {
      return {};
    }
    const userEmails: Record<string, string[]> = {};
    organizationSurveys.forEach(organizationSurvey => {
      const { organization } = organizationSurvey;
      const activeUsers = organization.users.filter(
        user =>
          user.active &&
          user.surveySeries?.some(surveySeries => surveySeries.name === organizationSurvey.surveySeriesName),
      );
      userEmails[`${organization.name} - ${organization.country?.name}`] = activeUsers.map(user => user.email);
    });
    return userEmails;
  };

  const getData = () => {
    setPageLoading(true);
    dispatch(
      getEmailById(emailId, res => {
        setPageLoading(false);
        if (!res) {
          setNotFound(true);
        } else {
          setEmailType(res.type);
          setEmailSubject(res.subject);
          setEmails({
            English: res.english,
            Arabic: res.arabic,
            Chinese: res.chinese,
            French: res.french,
            Russian: res.russian,
            Spanish: res.spanish,
          });
          setDbEmails({
            English: res.english,
            Arabic: res.arabic,
            Chinese: res.chinese,
            French: res.french,
            Russian: res.russian,
            Spanish: res.spanish,
          });
          setFilters(res.filters || []);
          setDBFilters(JSON.parse(JSON.stringify(res.filters || [])));
        }
      }),
    );
  };

  const handleLanguage = (lang: string) => {
    setLanguage(lang);
  };

  useEffect(() => {
    dispatch(getSurveySeries({ limit: 1000, page: 1 }));

    return () => {
      dispatch(resetOrganizationSurveys());
      dispatch(resetEmail());
    };
  }, []);

  useEffect(() => {
    setOrganizationSurveysLoading(true);
    dispatch(
      getOrganizationSurveys(
        {
          limit: -1,
          page: 1,
          filters,
          orderData: {
            orderBy: 'createdAt',
            sortDirection: 'asc',
          },
        },
        () => {
          setOrganizationSurveysLoading(false);
        },
      ),
    );
  }, [dispatch, filters, emailType]);

  useEffect(() => {
    if (editor.current) {
      editor.current.setData(emails[language] || '');
    }
  }, [language]);

  useEffect(() => {
    dispatch(getStaticValues());
    if (type === UPDATE_EMAIL) {
      if (location?.state?.message) {
        setToastType('success');
        setToast(location?.state?.message);
        window.history.replaceState({}, '');
      }
      getData();
    }
  }, []);

  useEffect(() => {
    const { surveySeriesNameExistsExactlyOne, surveyNameExistsExactlyOne } = getSurveyData();
    if (
      surveySeriesNameExistsExactlyOne &&
      surveyNameExistsExactlyOne &&
      emailType === PUBLISH &&
      type === CREATE_EMAIL
    ) {
      dispatch(
        getEmailByParams(
          {
            filters: [surveySeriesNameExistsExactlyOne, surveyNameExistsExactlyOne],
            type: PUBLISH,
          },
          res => {
            if (emailId === res.id) {
              setSelectedSurveyInfo({
                surveyId: res.surveyId,
                surveySeriesId: res.survey.surveySeriesId,
              });
              return;
            }
            navigate(`/emails/${res.id}`, {
              state: {
                message: 'Email with `Publish` type already exists for this survey',
              },
            });
          },
        ),
      );
    }
  }, [emailType, filters]);

  const getSurveys = () =>
    seriesList
      .map(el =>
        el.surveys
          .filter(item => item.name)
          .map(survey => ({
            id: survey.id,
            name: `${el.name} (${survey?.name})`,
            surveySeriesId: el.id,
            surveySeriesName: el.name,
          })),
      )
      .flat();

  useEffect(() => {
    if (!filterByField) {
      return;
    }
    if (filterByField.toLowerCase() === 'survey series name') {
      if (selectedSurvey?.id) {
        setFilterValues([
          seriesList.find(el => el.surveys.some(survey => survey.id === selectedSurvey.id))?.name || '',
        ]);
      } else {
        setFilterValues(seriesList.map(el => el.name));
      }
    } else {
      setFilterValues(
        getSurveys()
          .filter(el =>
            selectedSurveySeries?.surveySeriesId ? el.surveySeriesId === selectedSurveySeries?.surveySeriesId : true,
          )
          .map(el => el.name),
      );
    }
  }, [filterByField, seriesList]);

  const selectedSurvey = getSurveys().find(el => el.name === filters.find(item => item.field === 'Survey')?.values[0]);
  const selectedSurveySeries = getSurveys().find(
    el => el.surveySeriesName === filters.find(item => item.field.toLowerCase() === 'survey series name')?.values[0],
  );

  const surveyId = selectedSurvey?.id || selectedSurveyInfo.surveyId || '';
  const surveySeriesId = selectedSurveySeries?.surveySeriesId || selectedSurveyInfo.surveySeriesId || '';

  const addFilter = () => {
    if (!filterByValue || !filterByField || !filterOperation) {
      return;
    }
    const filter = filters.find(obj => obj.field === filterByField);
    if (filter) {
      filter.values.push(filterByValue);
    } else {
      filters.push({
        field: filterByField,
        values: [filterByValue],
        operation: filterOperation,
      });
    }
    setFilterByValue('');
    setFilterOperation('');
    setFilters([...filters]);
  };

  const deleteFilter = (filter: FILTERS, value: string) => {
    const foundFilter = filters.find(obj => obj.field === filter.field);
    foundFilter.values = foundFilter.values.filter(el => el !== value);
    if (!foundFilter.values.length) {
      setFilters(filters.filter(el => el.field !== filter.field));
    } else {
      setFilters([...filters]);
    }
  };

  const saveEmail = () => {
    const emailData = {};
    Object.entries(emails).forEach(([key, value]) => {
      if (value) {
        emailData[key] = value;
      }
    });
    if (!Object.entries(emailData).length) {
      setToastType('error');
      setToast('Please add email content');
      return;
    }
    if (!emailSubject) {
      setToastType('error');
      setToast('Please add email subject');
      return;
    }
    setLoading(true);
    if (type === CREATE_EMAIL) {
      dispatch(
        createEmail(
          {
            emailData,
            surveyId: organizationSurvey?.surveyId || surveyId,
            emailType,
            filters,
            emailSubject,
          },
          (res, message) => {
            setLoading(false);
            if (res) {
              navigate('/emails', {
                state: {
                  message: 'Email is created',
                },
              });
            } else {
              setToastType('error');
              setToast(message || 'Opps, unknown error occured.');
            }
          },
        ),
      );
    } else {
      dispatch(
        updateEmail(
          {
            id: emailId,
            emailData,
            surveyId: organizationSurvey?.surveyId || email?.survey?.id,
            filters,
            emailSubject,
          },
          (res, message) => {
            setLoading(false);
            if (res) {
              setToastType('success');
              setToast('Email is updated');
              getData();
            } else {
              setToastType('error');
              setToast(message || 'Opps, unknown error occured.');
            }
          },
        ),
      );
    }
  };

  const confirmSendEmail = () => {
    setConfirmLoading(true);
    dispatch(
      sendEmail(emailId, (res, message) => {
        setConfirmLoading(false);
        if (res) {
          navigate('/emails', {
            state: {
              message: 'Emails are sent',
            },
          });
        } else {
          setToastType('error');
          setToast(message || 'Opps, unknown error occured.');
        }
      }),
    );
  };

  const shouldShowFilters = () => {
    if (type === CREATE_EMAIL) {
      return !!emailType;
    }

    if (type === UPDATE_EMAIL) {
      return emailType === AD_HOC && email?.status !== SENT;
    }

    return false;
  };

  const toolbarItems = ['undo', 'redo', '|', 'heading', 'bold', 'italic', 'link'];
  const plugins = [Bold, Essentials, Italic, Mention, Paragraph, Undo, Link, Heading];
  const filteringOptions = (emailType === PUBLISH ? ['survey series name', 'survey'] : Object.keys(dropdownValues)).map(
    item => item.charAt(0).toUpperCase() + item.slice(1),
  );

  const filterValueLabelStyle = {
    marginRight: 1,
    paddingLeft: 1,
    paddingRight: 1,
    backgroundColor: '#3d82d0',
    color: '#fff',
  };

  const { surveySeriesNameExistsExactlyOne, surveyNameExistsExactlyOne, organizationSurvey } = getSurveyData();
  const surveyLink = `${window.location.origin}/survey-series/${surveySeriesId}/survey/${surveyId}`;

  const emailsToBeSentTo = getEmailsToBeSentTo();
  const emailSentDate = new Date(email?.updatedAt).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  if (notFound) {
    return <NoData message='No email found' />;
  }
  if (pageLoading) {
    return (
      <Box className='loader'>
        <CircularProgress size={40} color='inherit' />
      </Box>
    );
  }
  return (
    <Box className='list'>
      <Typography variant='h5'>Email {type === CREATE_EMAIL ? 'creation' : 'details'}</Typography>
      <Box className='list-header' sx={{ mb: 4, mt: 2 }}>
        <Dropdown
          value={emailType}
          label='Email type'
          options={emailTypes}
          onChange={(_: SyntheticEvent, value: string) => {
            setEmailType(value);
          }}
          style={type === UPDATE_EMAIL ? { pointerEvents: 'none' } : {}}
        />
        {(emailType === PUBLISH ? filters.length < 2 : true) && shouldShowFilters() ? (
          <Box display='flex' gap={2}>
            <Dropdown
              value={filterByField}
              label='Filter by field'
              options={filteringOptions}
              onChange={(_: SyntheticEvent, value: string) => {
                setFilterByField(value);
                setFilterByValue('');
              }}
            />
            <Dropdown
              value={filterOperation}
              label='Filter operation'
              options={(emailType === PUBLISH ? [FILTER_OPERATIONS[0]] : FILTER_OPERATIONS).filter(operation => {
                const filter = filters.find(filter => filter.field === filterByField);
                if (filter) {
                  return filter.operation === operation;
                }
                return true;
              })}
              onChange={(_: SyntheticEvent, value: string) => {
                setFilterOperation(value);
              }}
              disableClearable
            />
            <Dropdown
              value={filterByValue}
              label='Filter by value'
              options={
                (emailType === PUBLISH ? filterValues : dropdownValues[filterByField?.toLowerCase()] || []).filter(
                  item => !filters.some(filter => filter.field === filterByField && filter.values.includes(item)),
                ) || []
              }
              onChange={(_: SyntheticEvent, value: string) => setFilterByValue(value)}
              disabled={!filterByField}
            />
            <Button disabled={!filterByValue || !filterByField || !filterOperation} text='Add' onClick={addFilter} />
          </Box>
        ) : (
          type === UPDATE_EMAIL && (
            <Typography>
              {`${email?.survey?.surveySeries?.name} (${email?.survey?.name || 'N/A'}) `}
              <Chip
                label={`${email?.survey?.status} survey`}
                color={email?.survey?.status === DRAFT ? 'primary' : 'success'}
                sx={{ width: 145, fontSize: 15 }}
              />
            </Typography>
          )
        )}
      </Box>
      {shouldShowFilters() &&
        filters.map(filter => (
          <Box key={filter.field} display='flex' gap={1} alignItems='center' mb={2}>
            <Typography>{filter.field}</Typography>
            <Typography>{filter.operation.toLowerCase()}</Typography>
            <Box>
              {filter.values.map((value: string) => (
                <Chip
                  key={value}
                  label={value}
                  sx={filterValueLabelStyle}
                  size='small'
                  deleteIcon={<ClearIcon style={{ color: '#fff' }} />}
                  onDelete={() => deleteFilter(filter, value)}
                />
              ))}
            </Box>
          </Box>
        ))}
      {emailType === AD_HOC &&
        shouldShowFilters() &&
        (!surveySeriesNameExistsExactlyOne || !surveyNameExistsExactlyOne ? (
          <Box display='flex' mt={4} gap={1}>
            <InfoOutlined fontSize='small' />
            <Typography variant='caption'>
              Please select one survey series and one survey of it for which to generate and send email, you may add
              other filters as well
            </Typography>
          </Box>
        ) : !organizationSurvey ? (
          <Box display='flex' mt={4} gap={1}>
            <InfoOutlined fontSize='small' />
            <Typography variant='caption'>There is no organization survey for selected filters</Typography>
          </Box>
        ) : null)}
      {!!emailType &&
        ((emailType === AD_HOC ? !!organizationSurvey : filters.length >= 2) || type === UPDATE_EMAIL) && (
          <Box display='flex' flexDirection='column' gap={2} mt={4}>
            <Box display='flex' gap={2}>
              <Dropdown
                value={language}
                label='Language'
                options={languages}
                onChange={(_: SyntheticEvent, value: string) => handleLanguage(value)}
                disableClearable
              />
              <TextField
                label='Subject'
                id='subject'
                variant='outlined'
                sx={{ width: 300 }}
                value={emailSubject}
                onChange={e => setEmailSubject(e.target.value)}
                disabled={readonly}
              />
            </Box>
            {!readonly && (
              <Box display='flex'>
                <Typography variant='caption'>Click to copy survey link</Typography>
                <ContentCopyIcon
                  key='copy'
                  sx={{
                    fontSize: 15,
                    cursor: 'pointer',
                    margin: '0px 5px 0px 2px',
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(surveyLink);
                    setToastType('success');
                    setToast('Survey link copied');
                  }}
                />
                <Typography variant='caption'>
                  or{' '}
                  <Typography
                    variant='caption'
                    color='#3d82d0'
                    fontWeight='bold'
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      const newData = {
                        ...emails,
                        [language]: `${emails[language] || ''} <br /> <a href=${surveyLink}>Survey link</a>`,
                      };
                      setEmails(newData);
                      editor.current.setData(newData[language]);
                    }}
                  >
                    insert
                  </Typography>{' '}
                  directly
                </Typography>
              </Box>
            )}
            <Box>
              <CKEditor
                editor={ClassicEditor}
                config={{
                  toolbar: {
                    items: toolbarItems,
                  },
                  plugins,
                  initialData: emails[language] || '',
                }}
                onChange={(_, editor) => {
                  setEmails({ ...emails, [language]: editor.getData() });
                }}
                onReady={editorRef => {
                  editor.current = editorRef;
                }}
                disabled={readonly}
              />
            </Box>
          </Box>
        )}
      {type === UPDATE_EMAIL && email?.sentTo?.to?.length && !!Object.keys(emailsToBeSentTo).length && (
        <Box mt={2} mb={2} overflow='auto' sx={verticalScrollStyle}>
          <Typography variant='h6'>Email sent on {emailSentDate} to</Typography>
          <Box ml={2}>
            {Object.entries(emailsToBeSentTo).map(
              ([organization, emails]) =>
                !!emails.length &&
                email?.sentTo?.to.some(email => emails.includes(email)) && (
                  <Box key={organization} mb={2}>
                    <Typography variant='subtitle1'>{organization}</Typography>
                    {emails.map(
                      emailValue =>
                        email?.sentTo?.to.includes(emailValue) && (
                          <Chip key={emailValue} label={emailValue} sx={{ mr: 1, mt: 1 }} />
                        ),
                    )}
                  </Box>
                ),
            )}
          </Box>
        </Box>
      )}
      {email?.status !== SENT && !!Object.keys(emailsToBeSentTo).length && (
        <Box mt={2} mb={2} overflow='auto' sx={verticalScrollStyle}>
          <Typography variant='h6'>Emails will be sent to</Typography>
          <Box ml={2}>
            {Object.entries(emailsToBeSentTo).map(
              ([organization, emails]) =>
                !!emails.length && (
                  <Box key={organization} mb={2}>
                    <Typography variant='subtitle1'>{organization}</Typography>
                    {emails.map(email => (
                      <Chip key={email} label={email} sx={{ mr: 1, mt: 1 }} />
                    ))}
                  </Box>
                ),
            )}
          </Box>
        </Box>
      )}
      {
        <Box className='submit-button'>
          {!readonly && !!emailType && (emailType === AD_HOC ? !!organizationSurvey : filters.length >= 2) && (
            <Button text={type === CREATE_EMAIL ? 'Create' : 'Update'} onClick={saveEmail} loading={loading} />
          )}
          {type === UPDATE_EMAIL &&
            (organizationSurveysLoading ? (
              <CircularProgress size={40} color='inherit' />
            ) : (
              (emailType === AD_HOC ? !!organizationSurvey : email?.survey?.status === PUBLISHED) &&
              (email?.status !== SENT ? (
                <Button
                  text='Send'
                  color='success'
                  onClick={() => setConfirmationPopupText('Are you sure you want to send this email?')}
                  disabled={!isSameData}
                  tooltip={!isSameData ? 'You have unsaved changes, please save them in order to send emails.' : ''}
                  style={!isSameData ? { pointerEvents: 'auto' } : {}}
                />
              ) : (
                <Chip
                  label={`Email is ${email?.status}`}
                  color='success'
                  variant='outlined'
                  sx={{ width: 145, fontSize: 18 }}
                />
              ))
            ))}
        </Box>
      }
      <ToastMessage toast={toast} close={() => setToast('')} messageType={toastType} />
      <Confirmation
        confirm={confirmSendEmail}
        text={confirmationPopupText}
        close={() => setConfirmationPopupText('')}
        loading={confirmLoading}
      />
    </Box>
  );
}

export default EmailForm;
